/**
 * Platform HQ — Pre-launch Smoke Dashboard.
 *
 * One-glance health board: 7 service tiles + a one-tap critical pytest run.
 * Lives at /platform-admin/smoke.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { useToast } from '../hooks/use-toast';
import { usePlatformAuth } from '../context/PlatformAuthContext';
import {
  ShieldCheck, ArrowLeft, RefreshCw, CheckCircle2, XCircle, Loader2,
  Database, Bot, Mail, CreditCard, HardDrive, BookOpen, Sparkles, FlaskConical,
  Clock, ChevronRight, AlertCircle,
} from 'lucide-react';

const API = `${window.__BACKEND_URL__}/api`;

// Pretty labels + icons for every service the backend reports.
const SERVICE_META = {
  mongodb: { label: 'MongoDB', icon: Database, hint: 'Primary data store' },
  recaptcha: { label: 'reCAPTCHA v3', icon: Bot, hint: 'Sign-in bot detection' },
  ses: { label: 'AWS SES', icon: Mail, hint: 'Transactional email' },
  stripe: { label: 'Stripe', icon: CreditCard, hint: 'Subscriptions & billing' },
  s3: { label: 'AWS S3', icon: HardDrive, hint: 'Photo & file storage' },
  quickbooks: { label: 'QuickBooks Online', icon: BookOpen, hint: 'Per-tenant accounting sync' },
  emergent_llm: { label: 'Emergent LLM', icon: Sparkles, hint: 'AI card summarisation' },
};

const SERVICE_ORDER = ['mongodb', 'recaptcha', 'ses', 'stripe', 's3', 'quickbooks', 'emergent_llm'];

const fmtWhen = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
};

const fmtDuration = (s) => {
  if (s == null) return '—';
  if (s < 60) return `${s.toFixed(1)}s`;
  const m = Math.floor(s / 60);
  const r = Math.round(s % 60);
  return `${m}m ${r}s`;
};

function ServiceTile({ id, data }) {
  const meta = SERVICE_META[id] || { label: id, icon: AlertCircle, hint: '' };
  const Icon = meta.icon;
  const ok = data?.ok;
  const ringColor = ok ? '#10B981' : '#EF4444';   // emerald / red
  const dotColor = ok ? '#10B981' : '#EF4444';
  return (
    <div
      data-testid={`smoke-tile-${id}`}
      className="relative rounded-2xl p-5 transition-transform hover:-translate-y-0.5"
      style={{
        background: '#0F1923',
        border: `1px solid ${ok ? 'rgba(16, 185, 129, 0.35)' : 'rgba(239, 68, 68, 0.45)'}`,
        boxShadow: `0 0 0 1px ${ringColor}11, 0 12px 30px -18px ${ringColor}66`,
      }}
    >
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-3">
          <div
            className="w-10 h-10 rounded-xl flex items-center justify-center"
            style={{ background: ok ? 'rgba(16,185,129,0.12)' : 'rgba(239,68,68,0.14)' }}
          >
            <Icon className="w-5 h-5" style={{ color: ringColor }} />
          </div>
          <div>
            <div className="text-white font-semibold leading-tight">{meta.label}</div>
            <div className="text-xs" style={{ color: '#94A3B8' }}>{meta.hint}</div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span
            data-testid={`smoke-tile-status-${id}`}
            className="text-xs font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full"
            style={{
              color: ringColor,
              background: ok ? 'rgba(16,185,129,0.10)' : 'rgba(239,68,68,0.12)',
              border: `1px solid ${ok ? 'rgba(16,185,129,0.3)' : 'rgba(239,68,68,0.35)'}`,
            }}
          >
            {ok ? 'Healthy' : 'Down'}
          </span>
          <span className="w-2 h-2 rounded-full animate-pulse" style={{ background: dotColor }} />
        </div>
      </div>
      <div className="text-sm leading-relaxed" style={{ color: ok ? '#CBD5E1' : '#FCA5A5' }}>
        {data?.detail || '—'}
      </div>
      {data?.latency_ms != null && (
        <div className="mt-3 text-xs flex items-center gap-1.5" style={{ color: '#64748B' }}>
          <Clock className="w-3 h-3" />
          {data.latency_ms} ms
        </div>
      )}
    </div>
  );
}

export default function PlatformSmoke() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { admin, token } = usePlatformAuth();

  useEffect(() => { if (!admin) navigate('/platform-admin/signin'); }, [admin, navigate]);

  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const [services, setServices] = useState(null);
  const [servicesLoading, setServicesLoading] = useState(true);
  const [lastChecked, setLastChecked] = useState(null);

  // Pytest job state
  const [pytestJob, setPytestJob] = useState(null);
  const [pytestStarting, setPytestStarting] = useState(false);
  const [pytestRecent, setPytestRecent] = useState([]);
  const pollRef = useRef(null);

  const fetchServices = useCallback(async () => {
    setServicesLoading(true);
    try {
      const r = await axios.get(`${API}/platform/smoke/services`, { headers });
      setServices(r.data);
      setLastChecked(r.data.checked_at);
    } catch (err) {
      toast({
        title: 'Smoke check failed',
        description: err?.response?.data?.detail || err.message,
        variant: 'destructive',
      });
    } finally {
      setServicesLoading(false);
    }
  }, [headers, toast]);

  const fetchPytestRecent = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/platform/smoke/pytest`, { headers });
      setPytestRecent(r.data || []);
    } catch {
      // Non-fatal — history is just a convenience strip.
    }
  }, [headers]);

  useEffect(() => {
    fetchServices();
    fetchPytestRecent();
    // Auto-refresh service tiles every 60 s so the dashboard stays "live".
    const t = setInterval(fetchServices, 60_000);
    return () => clearInterval(t);
  }, [fetchServices, fetchPytestRecent]);

  // ── Pytest run + poll ──────────────────────────────────────────────────
  const startPytest = async () => {
    setPytestStarting(true);
    try {
      const r = await axios.post(`${API}/platform/smoke/pytest`, {}, { headers });
      setPytestJob(r.data);
      toast({
        title: 'Pytest started',
        description: `Running ${r.data.modules?.length || 'critical'} test modules — usually 30-60 s.`,
      });
      // Start polling
      if (pollRef.current) clearInterval(pollRef.current);
      pollRef.current = setInterval(async () => {
        try {
          const p = await axios.get(`${API}/platform/smoke/pytest/${r.data.job_id}`, { headers });
          setPytestJob(p.data);
          if (['passed', 'failed', 'errored'].includes(p.data.status)) {
            clearInterval(pollRef.current);
            pollRef.current = null;
            fetchPytestRecent();
            const tone = p.data.status === 'passed' ? 'default' : 'destructive';
            toast({
              title: `Pytest ${p.data.status}`,
              description:
                p.data.summary
                  ? `${p.data.summary.passed} passed · ${p.data.summary.failed} failed · ${p.data.summary.skipped} skipped`
                  : 'Run complete',
              variant: tone,
            });
          }
        } catch {
          // Keep trying; transient errors are fine.
        }
      }, 3000);
    } catch (err) {
      toast({
        title: 'Could not start pytest',
        description: err?.response?.data?.detail || err.message,
        variant: 'destructive',
      });
    } finally {
      setPytestStarting(false);
    }
  };

  useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current); }, []);

  // ── Derived ───────────────────────────────────────────────────────────
  const allGreen = services?.ok === true;
  const downCount = services
    ? SERVICE_ORDER.filter((k) => services.services?.[k] && !services.services[k].ok).length
    : 0;

  const pytestRunning = pytestJob && ['queued', 'running'].includes(pytestJob.status);
  const pytestSummary = pytestJob?.summary;

  // ── Render ────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen" style={{ background: '#0B1321', color: '#F8FAFC' }}>
      {/* Top bar */}
      <header
        className="border-b sticky top-0 z-10"
        style={{ background: '#0F1923', borderColor: '#1F2D3F' }}
      >
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              data-testid="smoke-back-btn"
              onClick={() => navigate('/platform-admin/dashboard')}
              className="p-2 rounded-lg hover:bg-slate-800 transition"
              aria-label="Back to dashboard"
            >
              <ArrowLeft className="w-5 h-5" style={{ color: '#94A3B8' }} />
            </button>
            <ShieldCheck className="w-6 h-6" style={{ color: '#1E88E5' }} />
            <div>
              <div className="text-lg font-bold tracking-tight">Pre-Launch Smoke</div>
              <div className="text-xs" style={{ color: '#94A3B8' }}>
                Live connectivity board · Last checked {fmtWhen(lastChecked)}
              </div>
            </div>
          </div>
          <Button
            data-testid="smoke-refresh-btn"
            onClick={fetchServices}
            disabled={servicesLoading}
            className="text-white"
            style={{ background: '#1E88E5' }}
          >
            {servicesLoading ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <RefreshCw className="w-4 h-4 mr-2" />
            )}
            Re-run all checks
          </Button>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-6 space-y-8">
        {/* Big status banner */}
        <Card
          data-testid="smoke-summary-card"
          className="overflow-hidden border-0"
          style={{
            background: allGreen
              ? 'linear-gradient(135deg, #064E3B 0%, #0F1923 70%)'
              : 'linear-gradient(135deg, #7F1D1D 0%, #0F1923 70%)',
          }}
        >
          <CardContent className="p-6 flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-4">
              {allGreen ? (
                <CheckCircle2 className="w-14 h-14" style={{ color: '#10B981' }} />
              ) : (
                <XCircle className="w-14 h-14" style={{ color: '#EF4444' }} />
              )}
              <div>
                <div className="text-2xl font-bold tracking-tight">
                  {servicesLoading && !services
                    ? 'Checking integrations…'
                    : allGreen
                      ? 'All systems healthy'
                      : `${downCount} service${downCount === 1 ? '' : 's'} need${downCount === 1 ? 's' : ''} attention`}
                </div>
                <div className="text-sm" style={{ color: '#CBD5E1' }}>
                  {allGreen
                    ? 'Production stack is ready for traffic. Auto-refreshes every minute.'
                    : 'Open the affected tile below for the error detail and resolution path.'}
                </div>
              </div>
            </div>
            <Badge
              data-testid="smoke-summary-badge"
              className="text-sm px-3 py-1.5"
              style={{
                background: allGreen ? 'rgba(16,185,129,0.18)' : 'rgba(239,68,68,0.2)',
                color: allGreen ? '#86EFAC' : '#FCA5A5',
                border: `1px solid ${allGreen ? 'rgba(16,185,129,0.4)' : 'rgba(239,68,68,0.5)'}`,
              }}
            >
              {SERVICE_ORDER.filter((k) => services?.services?.[k]?.ok).length}/{SERVICE_ORDER.length} green
            </Badge>
          </CardContent>
        </Card>

        {/* Service tiles */}
        <section>
          <h2 className="text-sm font-semibold uppercase tracking-wider mb-3" style={{ color: '#94A3B8' }}>
            Service connectivity
          </h2>
          <div
            data-testid="smoke-service-grid"
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4"
          >
            {SERVICE_ORDER.map((id) => (
              <ServiceTile key={id} id={id} data={services?.services?.[id]} />
            ))}
          </div>
        </section>

        {/* Pytest section */}
        <section>
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-sm font-semibold uppercase tracking-wider" style={{ color: '#94A3B8' }}>
              Critical test suite
            </h2>
            <Button
              data-testid="smoke-pytest-run-btn"
              onClick={startPytest}
              disabled={pytestStarting || pytestRunning}
              className="text-white"
              style={{ background: '#8B5CF6' }}
            >
              {pytestStarting || pytestRunning ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  {pytestRunning ? 'Running tests…' : 'Starting…'}
                </>
              ) : (
                <>
                  <FlaskConical className="w-4 h-4 mr-2" />
                  Run critical pytest suite
                </>
              )}
            </Button>
          </div>

          <Card style={{ background: '#0F1923', border: '1px solid #1F2D3F' }}>
            <CardContent className="p-5">
              {!pytestJob && pytestRecent.length === 0 && (
                <div className="text-sm py-4 text-center" style={{ color: '#64748B' }}>
                  No runs yet. Tap "Run critical pytest suite" to spot-check
                  <code className="px-1.5 py-0.5 ml-1 rounded text-xs" style={{ background: '#1A2433', color: '#CBD5E1' }}>
                    test_pre_deployment_health · test_go_live_readiness · test_tenant_guard
                  </code>
                </div>
              )}

              {pytestJob && (
                <div data-testid="smoke-pytest-current" className="mb-4">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2 text-sm">
                      {pytestRunning && <Loader2 className="w-4 h-4 animate-spin" style={{ color: '#8B5CF6' }} />}
                      {pytestJob.status === 'passed' && <CheckCircle2 className="w-4 h-4" style={{ color: '#10B981' }} />}
                      {(pytestJob.status === 'failed' || pytestJob.status === 'errored') && (
                        <XCircle className="w-4 h-4" style={{ color: '#EF4444' }} />
                      )}
                      <span className="font-semibold capitalize">{pytestJob.status}</span>
                      <span style={{ color: '#64748B' }}>· started {fmtWhen(pytestJob.started_at || pytestJob.queued_at)}</span>
                    </div>
                    {pytestSummary?.duration_s != null && (
                      <span className="text-xs" style={{ color: '#94A3B8' }}>
                        {fmtDuration(pytestSummary.duration_s)}
                      </span>
                    )}
                  </div>

                  {pytestSummary && (
                    <div className="flex gap-2 flex-wrap text-xs mt-3">
                      <span className="px-2 py-1 rounded" style={{ background: 'rgba(16,185,129,0.12)', color: '#86EFAC' }}>
                        {pytestSummary.passed} passed
                      </span>
                      <span className="px-2 py-1 rounded" style={{ background: 'rgba(239,68,68,0.12)', color: '#FCA5A5' }}>
                        {pytestSummary.failed} failed
                      </span>
                      <span className="px-2 py-1 rounded" style={{ background: 'rgba(245,158,11,0.12)', color: '#FCD34D' }}>
                        {pytestSummary.skipped} skipped
                      </span>
                      {pytestSummary.errors > 0 && (
                        <span className="px-2 py-1 rounded" style={{ background: 'rgba(239,68,68,0.18)', color: '#FCA5A5' }}>
                          {pytestSummary.errors} errors
                        </span>
                      )}
                    </div>
                  )}

                  {(pytestJob.status === 'failed' || pytestJob.status === 'errored') && pytestJob.stdout && (
                    <details className="mt-3">
                      <summary className="text-xs cursor-pointer" style={{ color: '#94A3B8' }}>
                        Show pytest output
                      </summary>
                      <pre
                        data-testid="smoke-pytest-output"
                        className="text-xs mt-2 p-3 rounded overflow-x-auto whitespace-pre-wrap"
                        style={{ background: '#0B1321', border: '1px solid #1F2D3F', color: '#CBD5E1', maxHeight: 320 }}
                      >
                        {pytestJob.stdout?.slice(-4000)}
                      </pre>
                    </details>
                  )}
                </div>
              )}

              {/* Recent runs strip */}
              {pytestRecent.length > 0 && (
                <div data-testid="smoke-pytest-history" className="border-t pt-3" style={{ borderColor: '#1F2D3F' }}>
                  <div className="text-xs uppercase tracking-wider mb-2" style={{ color: '#64748B' }}>
                    Recent runs
                  </div>
                  <div className="space-y-1.5">
                    {pytestRecent.map((r) => (
                      <div
                        key={r.id}
                        data-testid={`smoke-history-${r.id}`}
                        className="flex items-center justify-between text-xs py-1.5 px-2 rounded hover:bg-slate-800/40"
                      >
                        <div className="flex items-center gap-2">
                          {r.status === 'passed' && <CheckCircle2 className="w-3.5 h-3.5" style={{ color: '#10B981' }} />}
                          {(r.status === 'failed' || r.status === 'errored') && (
                            <XCircle className="w-3.5 h-3.5" style={{ color: '#EF4444' }} />
                          )}
                          {(r.status === 'queued' || r.status === 'running') && (
                            <Loader2 className="w-3.5 h-3.5 animate-spin" style={{ color: '#8B5CF6' }} />
                          )}
                          <span className="capitalize font-medium">{r.status}</span>
                          <ChevronRight className="w-3 h-3" style={{ color: '#475569' }} />
                          <span style={{ color: '#94A3B8' }}>
                            {r.summary
                              ? `${r.summary.passed} pass · ${r.summary.failed} fail`
                              : 'No summary'}
                          </span>
                        </div>
                        <div style={{ color: '#64748B' }}>
                          {fmtWhen(r.finished_at || r.queued_at)} · {r.started_by}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </section>
      </div>
    </div>
  );
}
